import {
	Button,
	CircularProgress,
	Grid,
	IconButton,
	TextField,
	Typography,
} from "@material-ui/core";
import AddCircleOutlineOutlinedIcon from "@material-ui/icons/AddCircleOutlineOutlined";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, get, useForm } from "react-hook-form";

import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import {
	CATALOG_GRADE_INITIAL_VALUES,
	CatalogGrade,
	GradeChemistryLimit,
} from "@/pages/steel/models/CatalogGrade";
import {
	copyChemistriesLimit,
	getCatGradeChemistryLimits,
	upsertCatalogGrade,
} from "@/pages/steel/repositories/CatalogGradeRepository";
import { ErrorModal } from "@dexteel/mesf-core";
import { AddElementModal } from "./AddElementModal";
import { CopyFromModal } from "./CopyFromModal";
import { ElementLimits, useTextFieldStyles } from "./ElementLimits";

type Props = {
	grade: CatalogGrade | null;
	modal: "create" | "update" | "delete" | "";
	refreshData: Function;
	onHide: ({
		shouldUpdate,
		close,
		entityId,
	}: {
		shouldUpdate: boolean;
		close: boolean;
		entityId?: number;
	}) => void;
};

export const GradeDetails = ({ grade, modal, refreshData, onHide }: Props) => {
	const [message, setNotificationMessage] = useState<string>("");
	const [error, setError] = useState<string>("");
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [showCopyFromModal, setShowCopyFromModal] = useState<boolean>(false);
	const classes = useTextFieldStyles();
	const [showNewElementModal, setShowNewElementModal] =
		useState<boolean>(false);
	const [chemistriesElements, setChemistriesElements] = useState<
		GradeChemistryLimit[]
	>([]);

	const [elementsSelected, setElementsSelected] = useState<
		GradeChemistryLimit[]
	>([]);

	const loadData = async () => {
		setIsLoading(true);
		setError("");
		const resp = await getCatGradeChemistryLimits(grade?.GradeId!);
		if (resp.ok) {
			const rows = get(
				resp,
				"data.tables[0].rows",
				[],
			) as GradeChemistryLimit[];
			setChemistriesElements(rows);
			setElementsSelected(rows);
		} else {
			setError(resp.message);
		}
		setIsLoading(false);
	};

	const {
		reset,
		handleSubmit,
		control,
		formState: { dirtyFields },
		watch,
	} = useForm<CatalogGrade>({
		mode: "onChange",
	});

	const isFormDirty =
		!(Object.keys(dirtyFields).length === 0) || modal == "create";

	const onSubmit: SubmitHandler<CatalogGrade> = async (data) => {
		setIsLoading(true);
		if (modal !== "delete") {
			const gradeResp = await upsertCatalogGrade({
				...data,
				GradeId: data.IsDuplicate ? null : data.GradeId,
			});
			if (gradeResp.ok) {
				const entityId = get(gradeResp, "data.tables[0].rows[0].Id", 0);
				if (entityId) {
					const copyResponse = await copyChemistriesLimit({
						source: data.GradeId!,
						target: entityId,
					});
				}
				setNotificationMessage("The grade was saved succesfully.");
				await refreshData();
				onHide({
					shouldUpdate: true,
					close: false,
					entityId,
				});
			} else {
				setError(gradeResp.message);
			}
		}
		setIsLoading(false);
	};

	useEffect(() => {
		if (modal !== "") {
			reset(grade || CATALOG_GRADE_INITIAL_VALUES);
			loadData().then(() => {});
			setIsLoading(false);
		}
	}, [modal, grade]);

	return (
		<>
			{grade === null && modal !== "create" ? (
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						height: "100%",
						width: "100%",
					}}
				>
					<Typography>Select a grade or create a new one</Typography>
				</div>
			) : (
				<form onSubmit={handleSubmit(onSubmit)}>
					<Grid item xs={12} container spacing={2}>
						<Grid item container spacing={2} style={{ margin: 0 }}>
							<Grid item container spacing={2} alignItems="center">
								<Grid item md={4}>
									<Controller
										name="GradeName"
										control={control}
										render={({ field }) => (
											<TextField
												variant="outlined"
												value={field.value || ""}
												onChange={field.onChange}
												size="small"
												fullWidth
												className={classes.input}
												label="Grade Name"
											/>
										)}
									/>
								</Grid>
								<Grid item md={8}>
									<Controller
										name="Comments"
										control={control}
										render={({ field }) => (
											<TextField
												variant="outlined"
												value={field.value || ""}
												onChange={field.onChange}
												size="small"
												fullWidth
												multiline
												rows={1}
												className={classes.input}
												label="Comments"
											/>
										)}
									/>
								</Grid>
							</Grid>
							<Grid
								item
								container
								spacing={2}
								alignItems="center"
								justifyContent="center"
							>
								<Grid item md={4}></Grid>
								<Grid item md={2}>
									<Typography align="center" style={{ fontWeight: 600 }}>
										MIN
									</Typography>
								</Grid>
								<Grid item md={2}>
									<Typography align="center" style={{ fontWeight: 600 }}>
										MAX
									</Typography>
								</Grid>
								<Grid item md={2}>
									<Typography align="center" style={{ fontWeight: 600 }}>
										AIM
									</Typography>
								</Grid>
								<Grid item md={2}></Grid>
							</Grid>
							<Grid
								item
								container
								spacing={1}
								alignItems="center"
								justifyContent="center"
								style={{
									overflowY: "auto",
									height: "55vh",
									borderTop: "1px solid #dae3e8",
								}}
							>
								{chemistriesElements.map((e, index) => (
									<Grid item container key={index}>
										<ElementLimits
											element={e}
											disabled={modal !== "update"}
											refreshData={() => {
												refreshData();
												loadData();
											}}
											setError={setError}
											setNotificationMessage={setNotificationMessage}
										/>
									</Grid>
								))}
							</Grid>
							<Grid item xs={12} container spacing={1} alignItems="center">
								<Grid item md={1} xs={12}>
									<IconButton
										color="primary"
										onClick={() => setShowNewElementModal(true)}
										title="Add new element"
										size="small"
									>
										<AddCircleOutlineOutlinedIcon
											style={{ cursor: "pointer" }}
										/>
									</IconButton>
								</Grid>
								<Grid item md={5} xs={12}>
									<Button
										fullWidth
										variant="outlined"
										color="primary"
										size="small"
										onClick={() => {
											const gradeName = watch("GradeName");
											onSubmit({
												...CATALOG_GRADE_INITIAL_VALUES,
												GradeName: gradeName,
											});
											setShowCopyFromModal(true);
										}}
									>
										Copy Chemistries Limits
									</Button>
								</Grid>
								<Grid item md={3} xs={12}>
									<Button
										fullWidth
										variant="contained"
										color="default"
										size="small"
										onClick={() => onHide({ shouldUpdate: false, close: true })}
									>
										Close
									</Button>
								</Grid>
								<Grid item md={3} xs={12}>
									<Button
										fullWidth
										startIcon={isLoading && <CircularProgress size="1rem" />}
										disabled={isLoading}
										variant="contained"
										color="primary"
										type="submit"
										size="small"
									>
										Save
									</Button>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</form>
			)}
			<NotificationSnackBar
				message={message}
				onHide={() => setNotificationMessage("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
			<CopyFromModal
				gradeId={grade?.GradeId!}
				show={showCopyFromModal}
				onHide={(id?: number | null) => {
					if (id) {
						refreshData();
					}
					setShowCopyFromModal(false);
				}}
				setNotificationMessage={setNotificationMessage}
				setError={setError}
			/>
			<AddElementModal
				gradeId={grade?.GradeId!}
				show={showNewElementModal}
				onHide={(newElements: boolean) => {
					if (newElements) {
						refreshData();
					}
					setShowNewElementModal(false);
				}}
				onSelectionChanged={setElementsSelected}
				elementsOfGrade={chemistriesElements}
				elementsSelected={elementsSelected}
				setNotificationMessage={setNotificationMessage}
				setError={setError}
			/>
		</>
	);
};
